/**
 * Admin → server HTTP client for operator commands (P5 W1 §2.2).
 *
 * Wire path: `POST /api/v1/admin/players/:id/commands`. The server's
 * commands.controller persists the row, broadcasts `command.issued` to
 * the player namespace, and returns the freshly-inserted
 * `CommandExecutionRow`. The admin SPA then listens for the matching
 * `command.status` push (via `adminWs.onCommandStatus`) to render the
 * ACK/completion toast.
 *
 * `credentials: 'include'` round-trips the `dcms_access` httpOnly
 * cookie set by /auth/login; same-origin via the Vite dev proxy.
 *
 * `X-Requested-With` matches the rest of the admin client surface
 * (see `apps/admin/src/api/client.ts`); the server's CSRF guard
 * accepts it as a same-origin signal.
 */

import type {
  CommandExecutionRow,
  PlayerCommandIssue,
} from '@displaycms/contracts';

const API_BASE = '/api/v1';

export async function issuePlayerCommand(
  playerId: string,
  body: PlayerCommandIssue,
): Promise<CommandExecutionRow> {
  const res = await fetch(`${API_BASE}/admin/players/${playerId}/commands`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'displaycms-admin',
    },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const errBody = (await res.json().catch(() => null)) as
      | { message?: string }
      | null;
    throw new Error(errBody?.message ?? `command_failed_${res.status}`);
  }
  return (await res.json()) as CommandExecutionRow;
}

/**
 * GET /api/v1/admin/players/:id/commands/:command_id — fetches the
 * persisted row by command_id. Used by ScreenshotViewerModal to read
 * `result` after the WS `command.status='completed'` event fires.
 */
export async function fetchPlayerCommand(
  playerId: string,
  commandId: string,
): Promise<CommandExecutionRow> {
  const res = await fetch(
    `${API_BASE}/admin/players/${playerId}/commands/${commandId}`,
    {
      method: 'GET',
      headers: { 'X-Requested-With': 'displaycms-admin' },
      credentials: 'include',
    },
  );
  if (!res.ok) {
    throw new Error(`command_fetch_failed_${res.status}`);
  }
  return (await res.json()) as CommandExecutionRow;
}
